import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  inject,
  viewChild,
} from '@angular/core';
import { input } from '@angular/core';

@Component({
  selector: 'ngx-draw-canvas',
  standalone: true,
  imports: [],

  template: `<canvas #canvas [width]="width()" [height]="height()"></canvas>`,
  styles: [':host { display: inline-block; }'],
})
export class NgxDrawCanvasComponent implements AfterViewInit, OnDestroy {
  private platformId = inject<any>(PLATFORM_ID);
  private _zone = inject(NgZone);
  canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  width = input(200);
  height = input(100);
  lineWidth = input(6);
  color = input('black');

  private hammer?: HammerManager | null;
  context?: CanvasRenderingContext2D | null;

  ngAfterViewInit() {
    this.hammer = this.bindHammer();
  }

  ngOnDestroy() {
    this.hammer?.destroy();
  }

  private bindHammer() {
    if (!isPlatformBrowser(this.platformId)) return null;

    const canvas = this.canvas();
    if (!canvas) return null;

    const context = canvas.nativeElement.getContext('2d');

    if (!context) return null;
    this.context = context;

    return this._zone.runOutsideAngular(() => {
      const hm = new Hammer.Manager(canvas.nativeElement);

      const pan = new Hammer.Pan({
        direction: Hammer.DIRECTION_ALL,
        threshold: 0,
      });

      hm.add(pan);

      let tmpXY: DOMRect;

      const getPenXY = (e: HammerInput): HammerPoint => {
        return {
          x: e.center.x - tmpXY.left,
          y: e.center.y - tmpXY.top,
        };
      };

      hm.on('panstart panmove panend pancancel', (e: HammerInput) => {
        switch (e.type) {
          case 'panstart':
            {
              tmpXY = canvas.nativeElement.getBoundingClientRect();
              const { x, y } = getPenXY(e);
              context.lineWidth = this.lineWidth();
              context.strokeStyle = this.color();
              context.beginPath();
              context.moveTo(x, y);
            }
            break;
          case 'panmove':
            {
              const { x, y } = getPenXY(e);
              context.lineTo(x, y);
              context.stroke();
            }
            break;
          case 'pancancel':
          case 'panend':
            context.closePath();
            break;
        }
      });

      return hm;
    });
  }

  /** clear canvas */
  clear() {
    if (!this.canvas()) return null;

    return this.context?.clearRect(
      0,
      0,
      this.canvas().nativeElement.width,
      this.canvas().nativeElement.height,
    );
  }

  /** return canvas base64 url */
  toDataURL(type?: string, quality?: any) {
    if (!this.canvas()) return null;

    return this.canvas().nativeElement.toDataURL(type, quality);
  }
}
